import {
  buildQuantitySetFromPair,
  convertCommercialQuantity,
  getFractionalPieces,
  getQuantitySetValue,
  isAllocationUnit,
  quantitySetForEnteredAmount,
  quantitySetToFields,
  roundCommercialQuantity,
  scaleQuantitySetToAmount,
} from '../../utils/commercialQuantity';
import type { QuantitySet, QuantityUnit } from '../../utils/commercialQuantity';
import type { AllocationUnit, ConversionSource } from '../../utils/dimensionConversion';

/** The quantity fields of one editable ZD line (card editor). */
export interface SupplierOrderLineQuantityFields extends ConversionSource {
  quantity: number;
  unit?: string | null;
  purchaseQuantity?: number | null;
  purchaseUnit?: string | null;
  conversionQuantity?: number;
  conversionUnit?: AllocationUnit | null;
  /**
   * The quantity set as it was SAVED (null for a new line). The saved amount
   * typed again keeps it; a changed amount converts from the line's own
   * dimensions (what the backend validates) and rescales this set only when the
   * dimensions cannot convert — so a second edit never compounds a rounding.
   */
  persistedSet?: QuantitySet | null;
  /** The saved amount, price-unit amount and conversion amount of that line (a new line: absent). */
  persistedQuantity?: number | null;
  persistedPurchaseQuantity?: number | null;
  persistedConversionQuantity?: number | null;
}

export interface SupplierOrderLineQuantityPatch {
  quantity: number;
  purchaseQuantity: number;
  conversionQuantity?: number;
  quantityPcs: number | null;
  quantityM2: number | null;
  quantityM3: number | null;
}

const proportional = (value: number | null | undefined, ratio: number): number | undefined => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? undefined
    : roundCommercialQuantity(Number(value) * ratio)
);

/**
 * Usability round 2026-10-05 (ZD card): the amount of a line is typed straight
 * into the items table in the line's own unit (e.g. m²), without the unit
 * conversion dialog. The price unit and the optional conversion follow the
 * same physical amount:
 * - a saved line typed with its saved amount keeps its persisted quantity set;
 *   a saved line whose dimensions cannot convert rescales that set (a missing
 *   sibling scales proportionally, like the backend `historicalPurchaseMatches`);
 * - any other changed amount (and a new line) converts from the line's own
 *   dimensions and builds a new set from that pair — the pair the backend
 *   validates (80,092 m³ → 1618,02 szt., never the saved ratio's 1618 szt.).
 * Returns null for a non-positive amount (the caller keeps the old value).
 */
export function rescaleSupplierOrderLineQuantity(
  line: SupplierOrderLineQuantityFields,
  nextQuantity: number | null | undefined,
): SupplierOrderLineQuantityPatch | null {
  const quantity = roundCommercialQuantity(Number(nextQuantity));
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  const unit = (line.unit || 'pcs') as QuantityUnit;
  const purchaseUnit = (line.purchaseUnit || unit) as QuantityUnit;
  const conversionUnit = line.conversionUnit ?? null;

  const snapshot = line.persistedSet && isAllocationUnit(unit)
    ? quantitySetForEnteredAmount(line.persistedSet, quantity, unit, line)
    : null;
  if (snapshot && isAllocationUnit(unit)) {
    const scaled = scaleQuantitySetToAmount(snapshot, quantity, unit);
    const savedQuantity = getQuantitySetValue(snapshot, unit);
    if (scaled && savedQuantity) {
      const ratio = quantity / savedQuantity;
      const purchaseQuantity = purchaseUnit === unit
        ? quantity
        : (isAllocationUnit(purchaseUnit) ? getQuantitySetValue(scaled, purchaseUnit) : null)
          ?? proportional(line.persistedPurchaseQuantity ?? line.purchaseQuantity, ratio)
          ?? quantity;
      const conversionQuantity = conversionUnit
        ? getQuantitySetValue(scaled, conversionUnit)
          ?? proportional(line.persistedConversionQuantity ?? line.conversionQuantity, ratio)
        : undefined;
      return {
        quantity,
        purchaseQuantity,
        conversionQuantity,
        ...(quantitySetToFields(scaled) as Pick<SupplierOrderLineQuantityPatch, 'quantityPcs' | 'quantityM2' | 'quantityM3'>),
      };
    }
  }

  const ratio = Number(line.quantity) > 0 ? quantity / Number(line.quantity) : 1;
  const convert = (target: QuantityUnit): number | undefined => {
    if (target === unit) return quantity;
    if (isAllocationUnit(unit) && isAllocationUnit(target)) {
      const converted = convertCommercialQuantity(quantity, unit, target, line);
      if (converted.value !== null && converted.value > 0) return converted.value;
    }
    return undefined;
  };
  const purchaseQuantity = convert(purchaseUnit) ?? proportional(line.purchaseQuantity, ratio) ?? quantity;
  const conversionQuantity = conversionUnit
    ? convert(conversionUnit) ?? proportional(line.conversionQuantity, ratio)
    : undefined;
  const set = buildQuantitySetFromPair({
    physicalQuantity: quantity,
    physicalUnit: unit,
    pricingQuantity: purchaseQuantity,
    pricingUnit: purchaseUnit,
    conversionQuantity,
    conversionUnit,
  }, line);
  return {
    quantity,
    purchaseQuantity,
    conversionQuantity,
    quantityPcs: set?.pcs ?? null,
    quantityM2: set?.m2 ?? null,
    quantityM3: set?.m3 ?? null,
  };
}

/**
 * A new line's unit was changed in the table (szt. / m² / m³): price in the
 * same unit, no conversion, a fresh set from the line's dimensions.
 */
export function changeNewSupplierOrderLineUnit(
  line: SupplierOrderLineQuantityFields,
  nextUnit: AllocationUnit,
): SupplierOrderLineQuantityPatch & { unit: AllocationUnit; purchaseUnit: AllocationUnit; conversionUnit: null } {
  const quantity = Number(line.quantity) > 0 ? roundCommercialQuantity(Number(line.quantity)) : 1;
  const set = buildQuantitySetFromPair({
    physicalQuantity: quantity,
    physicalUnit: nextUnit,
    pricingQuantity: quantity,
    pricingUnit: nextUnit,
  }, line);
  return {
    unit: nextUnit,
    purchaseUnit: nextUnit,
    conversionUnit: null,
    quantity,
    purchaseQuantity: quantity,
    conversionQuantity: undefined,
    quantityPcs: set?.pcs ?? null,
    quantityM2: set?.m2 ?? null,
    quantityM3: set?.m3 ?? null,
  };
}

/**
 * Whole pieces only (owner 2026-10-06): a ZD line in m²/m³ changed in the table
 * whose amount is not a whole number of szt. (80,092 m³ = 1618,02 szt.). The table
 * marks it and the save is refused until it is rounded in the „Przelicznik”.
 * A saved line left unchanged keeps its saved amounts and stays saveable.
 */
export function hasSupplierOrderLineFractionalPieces(line: SupplierOrderLineQuantityFields): boolean {
  const same = (a: unknown, b: unknown) => (a == null || a === '' ? null : roundCommercialQuantity(Number(a)))
    === (b == null || b === '' ? null : roundCommercialQuantity(Number(b)));
  if (line.persistedQuantity != null
    && same(line.quantity, line.persistedQuantity)
    && same(line.purchaseQuantity ?? line.quantity, line.persistedPurchaseQuantity)
    && same(line.conversionQuantity, line.persistedConversionQuantity)) return false;
  const purchaseUnit = line.purchaseUnit || line.unit;
  const explicitPieces = purchaseUnit === 'pcs' && line.purchaseQuantity != null
    ? line.purchaseQuantity
    : (line.conversionUnit === 'pcs' && line.conversionQuantity != null ? line.conversionQuantity : null);
  return getFractionalPieces(line.quantity, line.unit || 'pcs', line, explicitPieces) !== null;
}
